import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';

type UserAccountData = {
	teamType: string;
	role: string;
};

type FireAnalyticsErrorParams = {
	id: string;
	field: 'teamType' | 'role';
	error: SkipSentryError;
	unMatchedValue: string;
};

// Roles defined in user segmentation
const USER_SEGMENTATION_ROLE_DESIGNER = 'designer';
// Team types defined in user segmentation
export const USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE = 'customer-service';
export const USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT = 'it-support';
export const USER_SEGMENTATION_TEAM_TYPE_HR = 'human-resources';
export const USER_SEGMENTATION_TEAM_TYPE_FINANCE = 'finance';
export const USER_SEGMENTATION_TEAM_TYPE_LEGAL = 'legal';
export const USER_SEGMENTATION_TEAM_TYPE_MARKETING = 'marketing';
export const USER_SEGMENTATION_TEAM_TYPE_OPERATIONS = 'operations';
export const USER_SEGMENTATION_TEAM_TYPE_SALES = 'sales';
export const USER_SEGMENTATION_TEAM_TYPE_SOFTWARE = 'software-development';

// Unique identifier used where all roles in a team type are eligible
const ALL_ROLES_ELIGIBLE = 'all-roles-eligible';

export const USER_SEGMENTATION_MAPPING_UNKNOWN = 'unknown';
export const USER_SEGMENTATION_MAPPING_NONE = '';

export type UserSegmentationTeamTypes =
	| typeof USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE
	| typeof USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT
	| typeof USER_SEGMENTATION_TEAM_TYPE_HR
	| typeof USER_SEGMENTATION_TEAM_TYPE_FINANCE
	| typeof USER_SEGMENTATION_TEAM_TYPE_LEGAL
	| typeof USER_SEGMENTATION_TEAM_TYPE_MARKETING
	| typeof USER_SEGMENTATION_TEAM_TYPE_OPERATIONS
	| typeof USER_SEGMENTATION_TEAM_TYPE_SALES
	| typeof USER_SEGMENTATION_TEAM_TYPE_SOFTWARE;

export type UserSegmentationRoleTypes =
	| typeof USER_SEGMENTATION_ROLE_DESIGNER
	| typeof ALL_ROLES_ELIGIBLE;

type UserSegmentationMapping = UserSegmentationTeamTypes | UserSegmentationRoleTypes;

// @ts-expect-error - It seems to be missing some translations (for all-roles-eligible).
const USER_SEGMENTATION_CLIENT_MAPPING: Record<UserSegmentationMapping, string[]> = {
	[USER_SEGMENTATION_ROLE_DESIGNER]: [
		'Designer',
		'Disainer',
		'Diseñador',
		'Formgivare',
		'Nhà thiết kế',
		'Projektant',
		'Suunnittelija',
		'Tasarımcı',
		'Tervező',
		'Дизайнер',
		'นักออกแบบ',
		'デザイナー',
		'設計師',
		'设计师',
		'디자이너',
	],
	[USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE]: [
		'Asiakaspalvelu',
		'Atendimento ao cliente',
		'Customer Service',
		'Dịch vụ khách hàng',
		'Klantenservice',
		'Klienditeenindus',
		'Kundenservice',
		'Kundeservice',
		'Kundservice',
		'Müşteri Hizmetleri',
		'Obsługa klienta',
		'Service à la clientèle',
		'Servicio de atención al cliente',
		'Servizio clienti',
		'Serviço de apoio ao cliente',
		'Zákaznický servis',
		'Zákaznícky servis',
		'Ügyfélszolgálati',
		'Обслуживание клиентов',
		'Служба підтримки клієнтів',
		'บริการลูกค้า',
		'カスタマー サービス',
		'客户服务',
		'顧客服務',
		'고객 서비스',
	],
	[USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: [
		'Assistenza IT',
		'BT Desteği',
		'Hỗ trợ CNTT',
		'IT',
		'IT Support',
		'IT podpora',
		'IT サポート',
		'IT 支持',
		'IT 支援',
		'IT 지원',
		'IT_Support',
		'IT_støtte',
		'IT_support',
		'IT_tugi',
		'IT_tuki',
		'Informatikai támogatási',
		'Soporte de TI',
		'Suporte de TI',
		'Support IT',
		'Wsparcie IT',
		'ИТ-поддержка',
		'Служба технічної підтримки',
		'ฝ่ายสนับสนุน IT',
	],
	[USER_SEGMENTATION_TEAM_TYPE_FINANCE]: [
		'Ekonomi',
		'Finance',
		'Financeiro',
		'Financie',
		'Finans',
		'Finanse',
		'Finants',
		'Finanza',
		'Finanzas',
		'Finanzen',
		'Finanças',
		'Pénzügyi',
		'Rahoitus',
		'Tài chính',
		'Økonomi',
		'Финансы',
		'Фінанси',
		'การเงิน',
		'財務',
		'金融',
		'재무',
	],
	[USER_SEGMENTATION_TEAM_TYPE_HR]: [
		'HR',
		'Henkilöstöhallinto',
		'Human Resources',
		'Human resources',
		'human-resources', // for Upaas
		'Lidské zdroje',
		'Menneskelige ressourcer',
		'Nhân sự',
		'Personal',
		'Personalfrågor',
		'RH',
		'Recursos Humanos',
		'Recursos humanos',
		'Risorse umane',
		'Személyzeti',
		'Zasoby ludzkie',
		'İnsan Kaynakları',
		'Ľudské zdroje',
		'Відділ кадрів',
		'Кадровые вопросы',
		'ทรัพยากรบุคคล',
		'人事',
		'人力資源',
		'人力资源',
		'인사',
	],
	[USER_SEGMENTATION_TEAM_TYPE_LEGAL]: [
		'Departamento jurídico',
		'Hukuk',
		'Jogi',
		'Jura',
		'Juridik',
		'Juridisk',
		'Jurídico',
		'Legal',
		'Legale',
		'Oikeudelliset asiat',
		'Pháp lý',
		'Prawo',
		'Právne oddelenie',
		'Právní oddělení',
		'Rechtliches',
		'Service juridique',
		'Õigus',
		'Юридические вопросы',
		'Юридичний відділ',
		'กฎหมาย',
		'法務',
		'法律',
		'법률',
	],
	[USER_SEGMENTATION_TEAM_TYPE_MARKETING]: [
		'Markedsføring',
		'Marketing',
		'Markkinointi',
		'Marknadsföring',
		'Pazarlama',
		'Tiếp thị',
		'Turundus',
		'Маркетинг',
		'การตลาด',
		'マーケティング',
		'营销',
		'行銷',
		'마케팅',
	],
	[USER_SEGMENTATION_TEAM_TYPE_OPERATIONS]: [
		'Drift',
		'Haldus',
		'Operacional',
		'Operaciones',
		'Operacje',
		'Operasyonlar',
		'Operations',
		'Operativer Betrieb',
		'Operazioni',
		'Operações',
		'Opérations',
		'Prevádzka',
		'Provoz',
		'Toiminnot',
		'Vận hành',
		'Üzemeltetési',
		'Виробничий відділ',
		'Операционные вопросы',
		'ฝ่ายปฏิบัติการ',
		'オペレーション',
		'營運',
		'运营',
		'운영',
	],
	[USER_SEGMENTATION_TEAM_TYPE_SALES]: [
		'Bán hàng',
		'Försäljning',
		'Myynti',
		'Müük',
		'Predaj',
		'Prodej',
		'Sales',
		'Salg',
		'Satış',
		'Sprzedaż',
		'Vendas',
		'Vendite',
		'Ventas',
		'Ventes',
		'Vertrieb',
		'Értékesítési',
		'Продажи',
		'Продажі',
		'ฝ่ายขาย',
		'セールス',
		'銷售',
		'销售',
		'영업',
	],
	[USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: [
		'Desarrollo de software',
		'Desenvolvimento de software',
		'Développement logiciel',
		'Ohjelmistokehitys',
		'Phát triển phần mềm',
		'Programutveckling',
		'Programvareutvikling',
		'Software Development',
		'Softwareentwicklung',
		'Softwareontwikkeling',
		'Softwareudvikling',
		'Sviluppo software',
		'Szoftverfejlesztő',
		'Tarkvaraarendus',
		'Tworzenie oprogramowania',
		'Vývoj softvéru',
		'Vývoj softwaru',
		'Yazılım Geliştirme',
		'Разработка ПО',
		'Розробка ПЗ',
		'พัฒนาซอฟต์แวร์',
		'ソフトウェア開発',
		'軟體開發',
		'软件开发',
		'소프트웨어 개발',
	],
};

const BUSINESS_TEAM_TYPES: UserSegmentationTeamTypes[] = [
	USER_SEGMENTATION_TEAM_TYPE_HR,
	USER_SEGMENTATION_TEAM_TYPE_FINANCE,
	USER_SEGMENTATION_TEAM_TYPE_LEGAL,
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_SALES,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
];

const BUSINESS_ROLES_PER_TEAM_TYPE: Partial<
	Record<UserSegmentationTeamTypes, UserSegmentationRoleTypes[]>
> = {
	[USER_SEGMENTATION_TEAM_TYPE_HR]: [ALL_ROLES_ELIGIBLE],
	[USER_SEGMENTATION_TEAM_TYPE_FINANCE]: [ALL_ROLES_ELIGIBLE],
	[USER_SEGMENTATION_TEAM_TYPE_LEGAL]: [ALL_ROLES_ELIGIBLE],
	[USER_SEGMENTATION_TEAM_TYPE_MARKETING]: [ALL_ROLES_ELIGIBLE],
	[USER_SEGMENTATION_TEAM_TYPE_OPERATIONS]: [ALL_ROLES_ELIGIBLE],
	[USER_SEGMENTATION_TEAM_TYPE_SALES]: [ALL_ROLES_ELIGIBLE],
	[USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: [USER_SEGMENTATION_ROLE_DESIGNER],
};

const getNormalizedEntry = (userSegValue: string) => {
	const lowerCaseUserSegValue = userSegValue.toLowerCase();
	return Object.entries(USER_SEGMENTATION_CLIENT_MAPPING).find(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		([, translatedValues]: [any, string[]]) =>
			translatedValues.map((v) => v.toLowerCase()).includes(lowerCaseUserSegValue),
	);
};

export const normalizeUserSegmentationString = (value: string): string => {
	if (value === '') {
		return USER_SEGMENTATION_MAPPING_NONE;
	}
	const normalizedEntry = getNormalizedEntry(value);
	return normalizedEntry !== undefined ? normalizedEntry[0] : USER_SEGMENTATION_MAPPING_UNKNOWN;
};

class SkipSentryError extends Error {
	skipSentry: boolean;

	constructor(message: string) {
		super(message);
		this.skipSentry = true;
	}
}

const fireAnalyticsError = ({ id, field, error, unMatchedValue }: FireAnalyticsErrorParams) => {
	fireErrorAnalytics({
		meta: {
			id,
			packageName: 'jiraOnboardingCore',
		},
		attributes: {
			field,
			unMatchedValue,
		},
		error,
		sendToPrivacyUnsafeSplunk: true,
	});
};

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export const isInSoftwareTeam = (teamType: string | void): boolean => {
	if (!teamType) {
		return false;
	}
	const normalizedTeamType = normalizeUserSegmentationString(teamType);
	if (normalizedTeamType === USER_SEGMENTATION_MAPPING_UNKNOWN) {
		fireAnalyticsError({
			id: 'isInSoftwareTeam',
			field: 'teamType',
			error: new SkipSentryError('teamType mapping is not found'),
			unMatchedValue: teamType,
		});
	}
	return normalizedTeamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE;
};

export const userBelongsToBusinessUserSegCategory = (userAccountData: UserAccountData): boolean => {
	const { teamType = '', role = '' } = userAccountData;

	const normalizedTeamType = normalizeUserSegmentationString(teamType);
	if (normalizedTeamType === USER_SEGMENTATION_MAPPING_UNKNOWN) {
		fireAnalyticsError({
			id: 'userBelongsToBusinessUserSegCategory',
			field: 'teamType',
			error: new SkipSentryError('teamType mapping is not found'),
			unMatchedValue: teamType,
		});
	}

	const normalizedRole = normalizeUserSegmentationString(role);
	if (normalizedRole === USER_SEGMENTATION_MAPPING_UNKNOWN) {
		fireAnalyticsError({
			id: 'userBelongsToBusinessUserSegCategory',
			field: 'role',
			error: new SkipSentryError('role mapping is not found'),
			unMatchedValue: role,
		});
	}

	// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'UserSegmentationTeamTypes'.
	const isTeamTypeEligible = BUSINESS_TEAM_TYPES.includes(normalizedTeamType);
	const isRoleEligible: boolean =
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<Record<UserSegmentationTeamTypes, UserSegmentationRoleTypes[]>>'.
		BUSINESS_ROLES_PER_TEAM_TYPE[normalizedTeamType]?.includes(ALL_ROLES_ELIGIBLE) ||
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<Record<UserSegmentationTeamTypes, UserSegmentationRoleTypes[]>>'.
		BUSINESS_ROLES_PER_TEAM_TYPE[normalizedTeamType]?.includes(normalizedRole);

	return isTeamTypeEligible && isRoleEligible;
};

export const userBelongsToSoftwareUserSegCategory = (userAccountData: UserAccountData): boolean => {
	const { teamType = '', role = '' } = userAccountData;

	const normalizedTeamType = normalizeUserSegmentationString(teamType);
	if (normalizedTeamType === USER_SEGMENTATION_MAPPING_UNKNOWN) {
		fireAnalyticsError({
			id: 'userBelongsToSoftwareUserSegCategory',
			field: 'teamType',
			error: new SkipSentryError('teamType mapping is not found'),
			unMatchedValue: teamType,
		});
	}

	const normalizedRole = normalizeUserSegmentationString(role);

	if (normalizedRole === USER_SEGMENTATION_MAPPING_UNKNOWN) {
		fireAnalyticsError({
			id: 'userBelongsToSoftwareUserSegCategory',
			field: 'role',
			error: new SkipSentryError('role mapping is not found'),
			unMatchedValue: role,
		});
	}

	const isTeamTypeEligible = normalizedTeamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE;
	const isRoleEligible = normalizedRole !== USER_SEGMENTATION_ROLE_DESIGNER;

	return isTeamTypeEligible && isRoleEligible;
};
